<template>
  <NuxtLayout>
    <div class="min-h-screen bg-white text-purple-950">
      <AppHeader
        :mode="mode"
        @update:mode="onModeChange"
        @search-click="() => {}"
        @menu-click="() => {}"
        @login="onHeaderLogin"
        @how-it-works-click="() => {}"
      />

      <!-- Hero Section -->
      <section class="w-full px-4 pb-6 pt-10 sm:px-6">
        <div
          class="relative mx-auto h-56 w-full max-w-[1366px] overflow-hidden rounded-2xl sm:h-64"
        >
          <div
            class="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-700"
            aria-hidden="true"
          ></div>
          <div class="absolute inset-0 bg-black/20" aria-hidden="true"></div>
          <div
            class="relative z-10 flex h-full flex-col items-center justify-center space-y-3 px-4 py-10 text-center sm:px-6 sm:py-14"
          >
            <span class="text-4xl">🔒</span>
            <h1 class="text-3xl font-bold text-white sm:text-4xl md:text-5xl">
              {{ $t("privacy.title") }}
            </h1>
            <p
              class="max-w-2xl text-sm leading-relaxed text-white/80 sm:text-base md:text-lg"
            >
              {{ $t("privacy.description") }}
            </p>
          </div>
        </div>
      </section>

      <!-- Content -->
      <section class="w-full px-4 py-12">
        <div class="mx-auto w-full max-w-4xl space-y-6">
          <!-- Controller -->
          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 transition-all hover:border-purple-300 hover:shadow-lg md:p-8"
          >
            <h2
              class="mb-4 flex items-center gap-3 text-xl font-bold md:text-2xl"
            >
              <span
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-800 text-lg text-white"
                >🏢</span
              >
              {{ $t("privacy.controller") }}
            </h2>
            <p class="mb-2 leading-relaxed text-gray-700">
              {{ LEGAL_OPERATOR_NAME }}, {{ LEGAL_OPERATOR_ADDRESS }},
              {{ LEGAL_OPERATOR_CITY }}, Austria
            </p>
            <p class="leading-relaxed text-gray-700">
              {{ $t("imprint.emailLabel") }}
              <a
                v-if="hasPrivacyEmail"
                :href="privacyEmailHref"
                class="text-pink-500 underline hover:text-pink-600"
                >{{ LEGAL_PRIVACY_EMAIL }}</a
              >
              <span v-else>{{ LEGAL_PRIVACY_EMAIL }}</span>
            </p>
          </div>

          <!-- Data We Process -->
          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 transition-all hover:border-purple-300 hover:shadow-lg md:p-8"
          >
            <h2
              class="mb-4 flex items-center gap-3 text-xl font-bold md:text-2xl"
            >
              <span
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-pink-500 text-lg text-white"
                >📋</span
              >
              {{ $t("privacy.dataWeProcess") }}
            </h2>
            <ul class="space-y-2">
              <li class="flex items-start gap-2">
                <span class="mt-1 text-pink-500">•</span>
                <span class="text-gray-700">{{
                  $t("privacy.dataBullet1")
                }}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="mt-1 text-pink-500">•</span>
                <span class="text-gray-700">{{
                  $t("privacy.dataBullet2")
                }}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="mt-1 text-pink-500">•</span>
                <span class="text-gray-700">{{
                  $t("privacy.dataBullet3")
                }}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="mt-1 text-pink-500">•</span>
                <span class="text-gray-700">{{
                  $t("privacy.dataBullet4")
                }}</span>
              </li>
            </ul>
          </div>

          <!-- Purposes & Legal Basis -->
          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 transition-all hover:border-purple-300 hover:shadow-lg md:p-8"
          >
            <h2
              class="mb-4 flex items-center gap-3 text-xl font-bold md:text-2xl"
            >
              <span
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-800 text-lg text-white"
                >⚖️</span
              >
              {{ $t("privacy.purposesLegalBasis") }}
            </h2>
            <p class="leading-relaxed text-gray-700">
              {{ $t("privacy.legalBasisText") }}
            </p>
          </div>

          <!-- Processors & Transfers -->
          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 transition-all hover:border-purple-300 hover:shadow-lg md:p-8"
          >
            <h2
              class="mb-4 flex items-center gap-3 text-xl font-bold md:text-2xl"
            >
              <span
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-pink-500 text-lg text-white"
                >🌐</span
              >
              {{ $t("privacy.processorsTransfers") }}
            </h2>
            <p class="leading-relaxed text-gray-700">
              {{ $t("privacy.processorsText") }}
            </p>
          </div>

          <!-- Your Rights -->
          <div
            class="rounded-3xl border-2 border-purple-100 bg-white p-6 transition-all hover:border-purple-300 hover:shadow-lg md:p-8"
          >
            <h2
              class="mb-4 flex items-center gap-3 text-xl font-bold md:text-2xl"
            >
              <span
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-800 text-lg text-white"
                >✋</span
              >
              {{ $t("privacy.yourRights") }}
            </h2>
            <p class="leading-relaxed text-gray-700">
              {{ $t("privacy.rightsText") }}
            </p>
          </div>
        </div>
      </section>

      <!-- Footer -->
      <section class="w-full px-4 pb-12">
        <div class="mx-auto w-full max-w-[1366px]">
          <FooterMainPage
            @primary="onFooterPrimary"
            @secondary="onFooterSecondary"
          />
        </div>
      </section>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import AppHeader from "~/components/header/AppHeader.vue";
import FooterMainPage from "~/components/FooterMainPage.vue";

type Mode = "event" | "establishment";
const mode = ref<Mode>("event");
const localePath = useLocalePath();
const { t } = useI18n();
const {
  APP_URL,
  LEGAL_OPERATOR_NAME,
  LEGAL_OPERATOR_ADDRESS,
  LEGAL_OPERATOR_CITY,
  LEGAL_PRIVACY_EMAIL,
} = usePublicUrls();
const privacyEmailHref = computed(() => `mailto:${LEGAL_PRIVACY_EMAIL}`);
const hasPrivacyEmail = computed(() => !LEGAL_PRIVACY_EMAIL.startsWith("["));

const onModeChange = (m: Mode) => {
  mode.value = m;
};
const onHeaderLogin = () => {
  window.location.href = `${APP_URL}/`;
};
const onFooterPrimary = () => {
  navigateTo(localePath("/"));
};
const onFooterSecondary = () => {
  navigateTo(localePath("/"));
};

useSeoMeta({
  title: () => `${t("privacy.title")} - Upfest`,
  description: () => t("privacy.description"),
});
useStaticPageSeo("/privacy");
</script>
